import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { LogOut, Sun, Moon, ShieldCheck, Bell, Sliders, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

function Section({ icon: Icon, title, description, children, testId }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testId}>
      <div className="flex items-start gap-3 mb-4">
        <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-base">{title}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function Row({ label, right, testId }) {
  return (
    <div className="flex items-center justify-between py-2" data-testid={testId}>
      <span className="text-sm text-foreground/90">{label}</span>
      {right}
    </div>
  );
}

export default function Settings() {
  const { user, signOut } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [notify, setNotify] = useState(true);
  const [aiHedged, setAiHedged] = useState(true);
  const [aiRewrites, setAiRewrites] = useState(true);

  const handleSignOut = () => {
    signOut();
    nav("/", { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="settings-screen">
      <div>
        <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">Settings</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Preferences</h1>
      </div>

      <Section icon={User} title="Account" description="Your FINALLOOK identity." testId="settings-account">
        <Row label="Email" right={<span className="text-sm text-muted-foreground font-mono" data-testid="settings-account-email">{user?.email}</span>} />
        <Row label="Member since" right={<span className="text-sm text-muted-foreground">{user ? new Date(user.created_at).toLocaleDateString() : "—"}</span>} />
      </Section>

      <Section icon={theme === "dark" ? Moon : Sun} title="Appearance" description="Dark by default. Toggle if you prefer light." testId="settings-appearance">
        <Row
          label="Dark mode"
          right={<Switch checked={theme === "dark"} onCheckedChange={toggle} data-testid="settings-theme-toggle" />}
        />
      </Section>

      <Section icon={ShieldCheck} title="Privacy" description="Your messages are private. Only you can access them." testId="settings-privacy">
        <p className="text-sm text-muted-foreground leading-relaxed">
          FINALLOOK does not share your messages, does not offer public feeds, and does not let anyone browse another user&apos;s reviews. Reviews are stored tied to your account and can be deleted at any time.
        </p>
      </Section>

      <Section icon={Bell} title="Notifications" description="Alerts about your account." testId="settings-notifications">
        <Row
          label="Product updates"
          right={<Switch checked={notify} onCheckedChange={setNotify} data-testid="settings-notify-toggle" />}
        />
        <p className="text-xs text-muted-foreground pt-2">Push and email notifications are not yet available in this MVP.</p>
      </Section>

      <Section icon={Sliders} title="AI review preferences" description="How FINALLOOK responds." testId="settings-ai">
        <Row
          label="Use hedged language (may / could / might)"
          right={<Switch checked={aiHedged} onCheckedChange={setAiHedged} data-testid="settings-ai-hedged-toggle" />}
        />
        <Row
          label="Include suggested rewrites"
          right={<Switch checked={aiRewrites} onCheckedChange={setAiRewrites} data-testid="settings-ai-rewrites-toggle" />}
        />
        <p className="text-xs text-muted-foreground pt-2">Preference tuning is coming — these are placeholders.</p>
      </Section>

      <Button
        variant="outline"
        onClick={handleSignOut}
        className="w-full rounded-full h-12 border-border font-semibold"
        data-testid="settings-signout-btn"
      >
        <LogOut className="h-4 w-4 mr-2" /> Sign out
      </Button>
    </div>
  );
}
